import { useCallback, useEffect, useMemo } from 'react';
import { useCoreService } from 'core/services/useCoreService';
import { SearchTarjetaRequest } from 'core/tarjeta/models/SearchTarjetaRequest';
import { useDataFetcher } from 'shared/http';
import { Tarjeta } from 'core/tarjeta/models/Tarjeta';
import { Pageable } from 'shared/table/models/Pageable';
import { CardsTable } from '../CardsTable/CardsTable';
import { TablePagination } from 'shared/table/components/TablePagination/TablePagination';
import { withPagination } from 'shared/table/hoc/withPagination';

interface CardsTableWrappedProps {
  tableContent: Tarjeta[];
  pageable: Pageable<Tarjeta>;
  loadingState: boolean;
  onPaginationChange: (pageNumber: number, pageSize: number) => void;
  noResultsMessages: string;
  onChangeAction?: (nroCuenta: string, card: Tarjeta) => void;
  onDisassociateAction?: (nroCuenta: string, card: Tarjeta) => void;
  onAssociateAction?: (card: Tarjeta) => void;
  buttons?: boolean;
}

const BaseCardsTable = ({
  tableContent,
  pageable,
  loadingState,
  onPaginationChange,
  noResultsMessages,
  onChangeAction,
  onDisassociateAction,
  onAssociateAction,
  buttons,
}: CardsTableWrappedProps) => (
  <div>
    <CardsTable
      tableContent={tableContent}
      onChangeAction={onChangeAction}
      onDisassociateAction={onDisassociateAction}
      onAssociateAction={onAssociateAction}
      buttons={buttons ?? true}
    />

    {!loadingState && pageable && pageable.page.totalElements > 0 && (
      <TablePagination
        pageable={pageable}
        onChangePaginationStatus={(status) =>
          onPaginationChange(status.pageNumber, status.pageSize)
        }
      />
    )}

    {!loadingState && pageable && pageable.page.totalElements === 0 && (
      <div>{noResultsMessages}</div>
    )}
  </div>
);

const PaginatedBaseTable = withPagination(BaseCardsTable);

export interface CardsTableWithPaginationProps {
  searchParams: SearchTarjetaRequest;
  reloadToken?: number;
  onChangeAction?: (nroCuenta: string, card: Tarjeta) => void;
  onDisassociateAction?: (nroCuenta: string, card: Tarjeta) => void;
  onAssociateAction?: (card: Tarjeta) => void;
  onDataLoaded?: (data: Tarjeta[]) => void;
  onPaginationChange?: (pageNumber: number, pageSize: number) => void;
  buttons?: boolean;
  noResultsMessage?: string;
}

const DEFAULT_NO_RESULT_MESSAGE =
  'No se encontraron tarjetas que concuerden con los parámetros de la búsqueda';

export const CardsTableWithPagination = ({
  searchParams,
  reloadToken,
  onChangeAction,
  onDisassociateAction,
  onAssociateAction,
  onDataLoaded,
  onPaginationChange,
  buttons,
  noResultsMessage,
}: CardsTableWithPaginationProps) => {
  const coreService = useCoreService();
  const [tarjetas, fetchTarjetas] = useDataFetcher<Pageable<Tarjeta>>();

  const filtrosEstables = useMemo(() => {
    return searchParams;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchParams)]);

  useEffect(() => {
    fetchTarjetas(() => coreService.getAllTarjetas(filtrosEstables));
  }, [coreService, fetchTarjetas, filtrosEstables, reloadToken]);

  const fetchData = useCallback(
    async (pagination: {
      pageNumber: number;
      pageSize: number;
    }): Promise<Pageable<Tarjeta>> => {
      try {
        const response = await coreService.getAllTarjetas({
          ...searchParams,
          page: pagination.pageNumber,
          size: pagination.pageSize,
        });

        return response;
      } catch (error) {
        console.error('Error fetching cards:', error);
        return {
          content: [],
          page: {
            number: pagination.pageNumber,
            size: pagination.pageSize,
            totalElements: 0,
            totalPages: 1,
          },
        };
      }
    },

    [coreService, searchParams],
  );

  return (
    <PaginatedBaseTable
      dataFetcherFunction={fetchData}
      pageableInit={tarjetas.data}
      loadingState={tarjetas.isLoading}
      noResultsMessages={noResultsMessage || DEFAULT_NO_RESULT_MESSAGE}
      onChangeAction={onChangeAction}
      onDisassociateAction={onDisassociateAction}
      onAssociateAction={onAssociateAction}
      onPaginationChange={onPaginationChange}
      onDataLoaded={onDataLoaded}
      buttons={buttons}
    />
  );
};
